"use client";
/**
 * RecommendationStrip — "You might also like" horizontal carousel
 * (FR-019 Phase 1, online surface).
 *
 * Shows rec items (image, name, price, human-readable reason) for the given
 * trigger. Hides itself entirely when the branch is unknown, the request
 * fails, or the backend returns no items (kill switch / degraded) — recs
 * must never break or clutter a screen.
 *
 * Tap behaviour:
 *  1. Fires POST /recommendations/clicks (fire-and-forget).
 *  2. Adds the item through the EXISTING add-to-cart flow: items without
 *     variants/modifiers are added directly (same rule as the Home screen's
 *     quick-add "+"), otherwise we deep-link the item detail screen with the
 *     rec attribution in the query string so the customization flow attaches
 *     it on add.
 *  3. Attribution (recommendationId / source / surface / reasonCode / rank)
 *     rides on the CartLine and is threaded into the order-create payload by
 *     CheckoutScreen.
 */
import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { recommendationsApi, type RecommendationItem, type RecommendationTrigger } from "@/api/recommendationsApi";
import { FoodImg } from "@/components/FoodImg";
import { Icons } from "@/components/Icons";
import { Price } from "@/components/Price";
import { Card } from "@/components/ui";
import { useMenu, type UiMenuItem } from "@/hooks/useMenu";
import { useRecommendations } from "@/hooks/useRecommendations";
import { track } from "@/lib/analytics";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { useCart } from "@/store/cart";

function itemHasVariantsOrModifiers(item: UiMenuItem): boolean {
  const hasVariants = (item.raw?.variantGroups?.length ?? 0) > 0;
  const hasModifiers = (item.raw?.modifierGroups?.length ?? 0) > 0;
  return hasVariants || hasModifiers;
}

export function RecommendationStrip({
  trigger,
  style,
}: {
  trigger: RecommendationTrigger;
  /** Applied to the outer container ONLY when the strip actually renders —
   *  lets screens add spacing without leaving a gap when recs are empty. */
  style?: CSSProperties;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { branchId } = useBranch();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { items: menuItems } = useMenu();
  const { items: recs } = useRecommendations(trigger);

  // Cross-check against the loaded menu: drop recs for items the menu marks
  // unavailable so a tap can never add a sold-out item.
  const visibleRecs = useMemo(() => {
    if (recs.length === 0) return [];
    const byId = new Map(menuItems.map((it) => [it.id, it]));
    return recs.filter((r) => {
      const menuItem = byId.get(r.itemId);
      return !menuItem || menuItem.isAvailable;
    });
  }, [recs, menuItems]);

  if (!branchId || visibleRecs.length === 0) return null;

  const onPick = (rec: RecommendationItem) => {
    // 1. Click attribution — fire-and-forget, never blocks the add flow.
    recommendationsApi.recordClick({
      recommendationId: rec.recommendationId,
      itemId: rec.itemId,
      branchId,
      customerId: user?.id ?? undefined,
    });
    track("recommendation_clicked", {
      recommendation_id: rec.recommendationId,
      item_id: rec.itemId,
      trigger,
      source: rec.sourceCode,
      reason_code: rec.reasonCode,
      rank: rec.rank,
    });

    const attribution = {
      recommendationId: rec.recommendationId,
      recommendationSource: rec.sourceCode,
      recommendationSurface: "online",
      recommendationReasonCode: rec.reasonCode,
      recommendationRank: rec.rank,
    };

    // 2. Reuse the house add-to-cart rule (Home screen quick-add "+"): items
    //    without options go straight into the cart; anything needing
    //    customization routes through ItemDetailScreen, carrying the rec
    //    attribution in the query string.
    const menuItem = menuItems.find((it) => it.id === rec.itemId) ?? null;
    if (menuItem && !itemHasVariantsOrModifiers(menuItem)) {
      addToCart({
        id: `${menuItem.id}-${Date.now()}`,
        itemId: menuItem.id,
        name: menuItem.name,
        categoryId: menuItem.cat,
        categoryName: menuItem.categoryName,
        categoryNameRaw: menuItem.categoryNameRaw,
        price: menuItem.price,
        qty: 1,
        tone: menuItem.tone,
        imageUrl: menuItem.imageUrl,
        selectedModifiers: [],
        selectedVariants: [],
        modsLabel: [],
        notes: "",
        ...attribution,
      });
      return;
    }
    const params = new URLSearchParams({
      recId: rec.recommendationId,
      recSource: rec.sourceCode,
      recReasonCode: rec.reasonCode,
      recRank: String(rec.rank),
    });
    navigate(`/item/${rec.itemId}?${params.toString()}`);
  };

  return (
    <div style={style}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "var(--ink-2)",
          letterSpacing: 0.2,
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        {t("v2.recs.title", "You might also like")}
      </div>
      <div
        style={{
          display: "flex",
          gap: 10,
          overflowX: "auto",
          paddingBottom: 4,
          scrollbarWidth: "none",
        }}
      >
        {visibleRecs.map((rec) => {
          const menuItem = menuItems.find((it) => it.id === rec.itemId) ?? null;
          return (
            <Card
              key={rec.recommendationId}
              padding={0}
              onClick={() => onPick(rec)}
              style={{
                width: 148,
                flexShrink: 0,
                overflow: "hidden",
                cursor: "pointer",
              }}
            >
              <div style={{ padding: 8, paddingBottom: 0 }}>
                <FoodImg
                  tone={menuItem?.tone}
                  square
                  rounded={10}
                  label={rec.itemId}
                  src={rec.imageUrl ?? menuItem?.imageUrl}
                  alt={rec.name}
                />
              </div>
              <div style={{ padding: "8px 10px 10px", display: "flex", flexDirection: "column", gap: 3 }}>
                <div
                  className="display"
                  style={{
                    fontSize: 13,
                    color: "var(--ink-1)",
                    lineHeight: 1.2,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {rec.name}
                </div>
                {rec.reason && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--ink-3)",
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {rec.reason}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  <div className="num" style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink-1)" }}>
                    <Price amount={rec.price} />
                  </div>
                  <button
                    type="button"
                    aria-label={t("v2.recs.add", "Add")}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPick(rec);
                    }}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      border: 0,
                      background: "var(--primary-600)",
                      color: "#fff",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      boxShadow: "0 4px 10px -3px rgba(11,170,87,0.5)",
                    }}
                  >
                    <Icons.plus size={14} />
                  </button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
